import { useCallback, useEffect, useRef } from 'react';
import { ActivityTracker } from '@/services/ActivityTracker';
import { useLoggedInMember, useAuthActions } from '@/modules/auth/states';
import { useSocketData } from '@/shared/states/sockets';
import { deleteCookie } from '@/shared/utils/cookie-helper';
import { setLoginStep } from '@/modules/auth/states/login';
import { LOGIN_STEP } from '@/modules/auth/types';
import { ROUTES } from '@/shared/constants/routes';
import { authChannel } from '@/infra/auth/broadcast';
import { addNotification } from '@/shared/states/notification';

export const useSessionManagement = () => {
  const activityTrackerRef = useRef<ActivityTracker | null>(null);
  const loggedInMember = useLoggedInMember();
  const { setAuth } = useAuthActions();
  const mainSocketData = useSocketData('main');

  const handleForcedLogout = useCallback((message: string) => {
    
    // Stop activity tracking
    if (activityTrackerRef.current) {
      
      activityTrackerRef.current.cleanup();
      activityTrackerRef.current = null;
    }
    addNotification({
      type: 'info',
      message: `Logging out based on inactivity`,
      autoHideDuration: 5000,
    });

    // Delay actual logout by 5 seconds
    setTimeout(() => {
    
    // Clear authentication state
    deleteCookie('intermediate_session_token');
    setAuth({
      isAuthenticated: false,
      isAuthenticationInProgress: false,
      loggedInMember: null,
      loggedInOrganization: null,
    });
    
    setLoginStep(LOGIN_STEP.MAIN);
    
    // Broadcast logout to all tabs
    authChannel.postMessage("loggedOut");
  }, 5000);
  }, [setAuth]);

  // Listen for cross-tab logout events
  useEffect(() => {
    const handleCrossTabMessage = (event: MessageEvent) => {
      if (event.data === "loggedOut") {
        // Stop activity tracking when logout happens in another tab
        if (activityTrackerRef.current) {
          activityTrackerRef.current.cleanup();
          activityTrackerRef.current = null;
        }
      }
    };

    authChannel.onmessage = handleCrossTabMessage;

    return () => {
      authChannel.onmessage = null;
    };
  }, []);

  useEffect(() => {
    if (loggedInMember?.member_id && !activityTrackerRef.current) {
      // Initialize activity tracker when user is logged in
      activityTrackerRef.current = new ActivityTracker();
      activityTrackerRef.current.startTracking();
    } else if (!loggedInMember?.member_id && activityTrackerRef.current) {
      // Cleanup when user logs out
      activityTrackerRef.current.cleanup();
      activityTrackerRef.current = null;
    }

    return () => {
      if (activityTrackerRef.current) {
        activityTrackerRef.current.cleanup();
        activityTrackerRef.current = null;
      }
    };
  }, [loggedInMember?.member_id]);

  useEffect(() => {
    if (mainSocketData?.socket) {
      // Listen for forced logout events
      mainSocketData.socket.on('session:forced:logout', (data: { message: string }) => {
        handleForcedLogout(data.message);
      });

      return () => {
        mainSocketData.socket.off('session:forced:logout');
      };
    }
  }, [mainSocketData?.socket, handleForcedLogout]);

  return {
    isTracking: !!activityTrackerRef.current,
  };
};
